import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { useZona } from "@/context/ZonaContext";
import {
  Loader2, Download, FileSpreadsheet, FileText as FileTextIcon, Presentation,
  TrendingUp, TrendingDown, Minus, AlertTriangle, ExternalLink,
  Lock, History, Send, RotateCcw, X, ChevronDown, ChevronUp, ShieldCheck, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { DrillDownModal, BUCKET_LABELS } from "@/pages/YtdBoard";
import ComparativaAnualCard from "@/components/historico/ComparativaAnualCard";
import HistoricoImportModal from "@/components/historico/HistoricoImportModal";

/**
 * Entrega D (roadmap-valoriza-revision-despliegue-2026-10-12) · Business
 * Review mensual — panel interactivo, alimentado por el MISMO endpoint que
 * la exportación descargable (misma fuente/fórmulas/corte que el YTD Board
 * oficial). Bloque US/WT prioritario, comparativas mes anterior / mismo
 * periodo año anterior / presupuesto, con drill-down a contrato real.
 *
 * T15 (tanda integral consolidación, 12/10/2026) · además del panel en
 * vivo (que NUNCA cambia de comportamiento), se añade el "Cierre" —objeto
 * inmutable versionado, con borrador automático día 1 07:00 Europe/Madrid
 * y publicación interna explícita (ver CierrePanel más abajo).
 */

// Mismo conjunto que ADMIN_ROLES en backend/core/auth.py — sólo para decidir
// qué botones mostrar; el backend es la única fuente de verdad de permisos.
const ROLES_PUBLICACION = new Set(["admin", "it_validator", "offer_manager", "cfo", "director", "controller", "responsable_operativo"]);

const fmtMM = (v) => (v == null ? "N/D" : `${Number(v).toLocaleString("es-ES", { maximumFractionDigits: 2 })} M€`);
const fmtPct = (v) => (v == null ? "N/D" : `${v.toFixed(1)}%`);
const MESES = ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function Delta({ actual, base, label }) {
  if (actual == null || base == null) return <span className="text-[10px] text-gray-300">— {label}: N/D</span>;
  const diff = actual - base;
  const pct = base !== 0 ? (diff / Math.abs(base)) * 100 : null;
  const Icon = Math.abs(diff) < 0.01 ? Minus : diff > 0 ? TrendingUp : TrendingDown;
  const cls = Math.abs(diff) < 0.01 ? "text-gray-400" : diff > 0 ? "text-emerald-600" : "text-rose-600";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${cls}`} data-testid={`br-delta-${label}`}>
      <Icon size={10} /> {label}: {diff > 0 ? "+" : ""}{diff.toFixed(2)} M€{pct != null && ` (${pct > 0 ? "+" : ""}${pct.toFixed(0)}%)`}
    </span>
  );
}

// T15 · Panel de cierre inmutable del periodo/ámbito seleccionado. Nunca
// vuelve a leer `ofertas_comercial` directamente: sólo lista/crea/publica/
// corrige documentos de `business_review_cierres` (foto fija congelada).
function CierrePanel({ year, month, dg, puedePublicar }) {
  const [cierres, setCierres] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showHistorial, setShowHistorial] = useState(false);
  const [showCorregir, setShowCorregir] = useState(false);
  const [motivo, setMotivo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/business-review/cierres`, { params: { year, month, direccion_general: dg } });
      setCierres(data.cierres || []);
    } catch (e) {
      toast.error("No se pudo cargar el estado del cierre");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [year, month, dg]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="br-cierre-panel">
      <div className="flex items-center gap-2 text-xs text-gray-400 py-3"><Loader2 className="animate-spin" size={12} /> Comprobando cierre…</div>
    </div>
  );

  const actual = cierres?.[0] || null; // orden desc por versión → la [0] es la vigente

  const generar = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/business-review/cierres/generar-borrador`, null, { params: { year, month, direccion_general: dg } });
      toast.success("Borrador de cierre generado");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar el borrador");
    } finally {
      setBusy(false);
    }
  };

  const publicar = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/business-review/cierres/${actual.id}/publicar`);
      toast.success("Cierre publicado");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo publicar el cierre");
    } finally {
      setBusy(false);
    }
  };

  const corregir = async () => {
    if (!motivo.trim()) { toast.error("Indica el motivo de la corrección"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/business-review/cierres/${actual.id}/corregir`, { motivo });
      toast.success("Nueva versión creada como borrador — publícala cuando esté lista");
      setShowCorregir(false); setMotivo("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo corregir el cierre");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="br-cierre-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
          {actual?.estado === "publicado" ? <Lock size={12} className="text-[#007934]" /> : <AlertTriangle size={12} className="text-amber-500" />}
          Cierre del periodo
        </h3>
        {cierres?.length > 0 && (
          <button onClick={() => setShowHistorial(s => !s)} data-testid="br-cierre-ver-historial"
            className="text-[11px] text-gray-500 hover:text-gray-800 flex items-center gap-1">
            <History size={11} /> Historial ({cierres.length}) {showHistorial ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
        )}
      </div>

      {!actual ? (
        <div className="mt-2 flex items-center justify-between gap-2 flex-wrap" data-testid="br-cierre-sin-generar">
          <p className="text-[11px] text-gray-400">Sin cierre generado para este periodo/ámbito — se genera un borrador automático el día 1 a las 07:00 (Europe/Madrid).</p>
          {puedePublicar && (
            <button onClick={generar} disabled={busy} data-testid="br-cierre-generar-btn"
              className="text-[11px] font-semibold bg-gray-900 text-white rounded-lg px-2.5 py-1.5 hover:bg-gray-700 disabled:opacity-50 whitespace-nowrap">
              Generar borrador ahora
            </button>
          )}
        </div>
      ) : actual.estado === "borrador" ? (
        <div className="mt-2 space-y-2" data-testid="br-cierre-borrador">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-[11px] text-amber-700">
              <b>Borrador v{actual.version}</b> pendiente de publicación — creado {actual.creado_por_nombre === "Sistema (automático)" ? "automáticamente" : `por ${actual.creado_por_nombre}`} el {actual.creado_en?.slice(0, 10)}.
              {actual.motivo_correccion && <span className="block italic mt-0.5">Motivo de la corrección: {actual.motivo_correccion}</span>}
            </p>
            {puedePublicar && (
              <button onClick={publicar} disabled={busy} data-testid="br-cierre-publicar-btn"
                className="flex items-center gap-1 text-[11px] font-semibold bg-[#007934] text-white rounded-lg px-2.5 py-1.5 hover:bg-[#00632a] disabled:opacity-50 whitespace-nowrap">
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Publicar cierre
              </button>
            )}
          </div>
          {actual.incidencias?.length > 0 && (
            <ul className="text-[10px] text-amber-600 list-disc list-inside space-y-0.5" data-testid="br-cierre-incidencias">
              {actual.incidencias.map((inc, i) => <li key={i}>{inc}</li>)}
            </ul>
          )}
        </div>
      ) : (
        <div className="mt-2 space-y-2" data-testid="br-cierre-publicado">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-[11px] text-gray-600 flex items-center gap-1">
              <ShieldCheck size={12} className="text-[#007934]" />
              <b>Cierre publicado (v{actual.version})</b> el {actual.publicado_en?.slice(0, 10)} por {actual.publicado_por_nombre}. Inmutable — un sync posterior ya no puede cambiar estas cifras.
            </p>
            {puedePublicar && (
              <button onClick={() => setShowCorregir(true)} data-testid="br-cierre-corregir-btn"
                className="flex items-center gap-1 text-[11px] font-semibold border border-gray-300 text-gray-700 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 whitespace-nowrap">
                <RotateCcw size={11} /> Corregir (nueva versión)
              </button>
            )}
          </div>
          {actual.incidencias?.length > 0 && (
            <ul className="text-[10px] text-amber-600 list-disc list-inside space-y-0.5">
              {actual.incidencias.map((inc, i) => <li key={i}>{inc}</li>)}
            </ul>
          )}
        </div>
      )}

      {showHistorial && (
        <div className="mt-3 border-t border-gray-100 pt-2 space-y-1.5" data-testid="br-cierre-historial">
          {cierres.map((c) => (
            <div key={c.id} className="text-[10px] text-gray-500 flex items-center gap-2 flex-wrap">
              <span className={`px-1.5 py-0.5 rounded font-semibold ${c.estado === "publicado" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>v{c.version} · {c.estado}</span>
              <span>{c.creado_en?.slice(0, 10)} · {c.creado_por_nombre}</span>
              {c.publicado_en && <span>→ publicado {c.publicado_en.slice(0, 10)} por {c.publicado_por_nombre}</span>}
              {c.motivo_correccion && <span className="italic">· {c.motivo_correccion}</span>}
              {c.delta && (
                <span className="font-mono">Δ adjudicado: {c.delta.importe_adjudicado_mm?.diferencia ?? "N/D"} M€</span>
              )}
            </div>
          ))}
        </div>
      )}

      {showCorregir && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="br-cierre-corregir-modal">
          <div className="bg-white rounded-xl p-4 w-full max-w-md space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900">Corregir cierre publicado</h4>
              <button onClick={() => setShowCorregir(false)} aria-label="Cerrar" className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            <p className="text-[11px] text-gray-500">Se creará la versión v{actual?.version + 1} con los datos actuales, sin modificar la v{actual?.version} publicada. La nueva versión queda como borrador hasta que la publiques.</p>
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Motivo de la corrección (obligatorio)"
              data-testid="br-cierre-corregir-motivo" className="w-full text-xs border border-gray-200 rounded-lg p-2" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowCorregir(false)} className="text-xs text-gray-500 px-3 py-1.5">Cancelar</button>
              <button onClick={corregir} disabled={busy} data-testid="br-cierre-corregir-confirmar"
                className="text-xs font-semibold bg-gray-900 text-white rounded-lg px-3 py-1.5 disabled:opacity-50">
                {busy ? "Creando…" : "Crear nueva versión"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BusinessReview() {
  const { user } = useAuth();
  const { zonaGlobal } = useZona();
  const puedePublicar = ROLES_PUBLICACION.has(user?.role);
  const now = new Date();
  const [year, setYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth()); // mes CERRADO anterior por defecto
  const [dg, setDg] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [drillCtx, setDrillCtx] = useState(null);
  const [showHist, setShowHist] = useState(false);
  const [histKey, setHistKey] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year, month };
      if (dg) params.direccion_general = dg;
      if (zonaGlobal) params.zona = zonaGlobal;
      const { data } = await axios.get(`${API}/business-review/mensual`, { params });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar el Business Review");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [year, month, dg, zonaGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportar = async (formato) => {
    setExporting(formato);
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month), formato });
      if (dg) params.append("direccion_general", dg);
      if (zonaGlobal) params.append("zona", zonaGlobal);
      const resp = await axios.get(`${API}/business-review/export?${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `business_review_${year}-${String(month).padStart(2, "0")}_${dg || "consolidado"}.${formato}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo generar el export");
    } finally {
      setExporting(null);
    }
  };

  const resumen = data?.actual?.resumen;
  const comp = data?.comparativas;
  const presu = comp?.presupuesto;

  const bucketOptions = useMemo(() => Object.keys(BUCKET_LABELS), []);

  return (
    <div className="space-y-4" data-testid="business-review-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 font-['Outfit']">Business Review mensual</h1>
          <p className="text-xs text-gray-500">Mismos datos, fórmulas y versión de cierre que el YTD Board oficial.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5" data-testid="br-select-month">
            {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5" data-testid="br-select-year">
            {[now.getFullYear(), now.getFullYear() - 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={dg} onChange={(e) => setDg(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5" data-testid="br-select-dg">
            <option value="">Consolidado grupo (US+WT+Resto)</option>
            <option value="US">US · Servicios Urbanos</option>
            <option value="WT">WT · Tratamiento</option>
          </select>
          {[["pdf", FileTextIcon], ["pptx", Presentation], ["xlsx", FileSpreadsheet]].map(([fmt, Icon]) => (
            <button key={fmt} onClick={() => exportar(fmt)} disabled={exporting === fmt} data-testid={`br-export-${fmt}`}
              className="flex items-center gap-1 text-xs font-semibold bg-gray-900 text-white rounded-lg px-2.5 py-1.5 hover:bg-gray-700 disabled:opacity-50">
              {exporting === fmt ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />} {fmt.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {zonaGlobal && (
        <div className="flex items-start gap-2 bg-[#F0FDF4] border border-[#007934]/30 rounded-lg px-3 py-2 text-xs text-[#065f30]" data-testid="br-zona-banner">
          <MapPin size={14} className="mt-0.5 flex-shrink-0 text-[#007934]" />
          <span>
            Filtrado por <strong>zona comercial</strong>. El presupuesto se fija a nivel global/DG, no por zona: la comparativa <strong>vs presupuesto no aplica</strong> con este filtro.
          </span>
        </div>
      )}

      <CierrePanel year={year} month={month} dg={dg} puedePublicar={puedePublicar} />

      {/* Comparativa interanual desde histórico importado (año anterior vs en curso) */}
      <ComparativaAnualCard year={year} onImport={() => setShowHist(true)} refreshKey={histKey} />
      {showHist && <HistoricoImportModal onClose={() => setShowHist(false)} onImported={() => setHistKey(k => k + 1)} />}


      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-10"><Loader2 className="animate-spin" size={16} /> Cargando…</div>
      ) : !data ? (
        <div className="text-sm text-gray-400 italic">Sin datos.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="br-kpi-cards">
            <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="br-kpi-presentado">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Importe presentado</p>
              <p className="text-xl font-bold text-gray-900">{fmtMM(resumen?.importe_presentado_mm)}</p>
              <div className="flex flex-col gap-0.5 mt-1">
                <Delta actual={resumen?.importe_presentado_mm} base={comp?.mes_anterior?.resumen?.importe_presentado_mm} label="vs mes ant." />
                <Delta actual={resumen?.importe_presentado_mm} base={comp?.mismo_periodo_anio_anterior?.resumen?.importe_presentado_mm} label="vs mismo periodo año ant." />
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="br-kpi-adjudicado">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Importe adjudicado</p>
              <p className="text-xl font-bold text-[#007934]">{fmtMM(resumen?.importe_adjudicado_mm)}</p>
              <div className="flex flex-col gap-0.5 mt-1">
                <Delta actual={resumen?.importe_adjudicado_mm} base={comp?.mes_anterior?.resumen?.importe_adjudicado_mm} label="vs mes ant." />
                {presu?.cargado ? (
                  <Delta actual={resumen?.importe_adjudicado_mm} base={presu.importe_mm} label="vs presupuesto" />
                ) : (
                  <span className="flex items-center gap-1 text-[10px] text-amber-600" data-testid="br-presupuesto-sin-cargar">
                    <AlertTriangle size={10} /> Sin presupuesto cargado para este periodo
                  </span>
                )}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="br-kpi-adjudicaciones">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Nº adjudicaciones</p>
              <p className="text-xl font-bold text-gray-900">{resumen?.num_adjudicaciones ?? "N/D"}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="br-kpi-hitrate">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Hit rate</p>
              <p className="text-xl font-bold text-gray-900">{fmtPct(resumen?.hit_rate_pct)}</p>
            </div>
          </div>

          {/* CRM-R01 (E10) · Desglose explícito US / WT / Resto del consolidado.
              El "Resto" (DG distintas de US/WT, p.ej. SURGE, o sin DG) se muestra
              APARTE — nunca repartido ni mezclado en silencio. */}
          {data.desglose_ambito && (
            <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="br-desglose-ambito">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Desglose del consolidado por ámbito</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[["us", "US · Servicios Urbanos"], ["wt", "WT · Tratamiento"], ["resto", "Resto (SURGE / sin DG)"]].map(([k, label]) => {
                  const s = data.desglose_ambito[k];
                  return (
                    <div key={k} className={`rounded-lg border p-3 ${k === "resto" ? "border-amber-200 bg-amber-50/40" : "border-gray-100 bg-gray-50"}`} data-testid={`br-ambito-${k}`}>
                      <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">Presentado {fmtMM(s.importe_presentado_mm)}</p>
                      <p className="text-xs text-[#007934] font-semibold">Adjudicado {fmtMM(s.importe_adjudicado_mm)} · {s.num_adjudicaciones} adj.</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-gray-400 mt-2 flex items-start gap-1"><AlertTriangle size={10} className="mt-0.5 shrink-0" /> {data.desglose_ambito.nota}</p>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 p-4 overflow-x-auto">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Comparativas · {data.periodo} · {data.direccion_general}</h3>
            <table className="text-xs w-full" data-testid="br-tabla-comparativas">
              <thead>
                <tr className="text-gray-400 text-[10px] uppercase">
                  <th className="text-left py-1">Métrica</th>
                  <th className="text-right py-1">Actual ({data.periodo})</th>
                  <th className="text-right py-1">Mes anterior ({comp.mes_anterior.periodo})</th>
                  <th className="text-right py-1">Mismo periodo año ant. ({comp.mismo_periodo_anio_anterior.periodo})</th>
                  <th className="text-right py-1">Presupuesto</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-100">
                  <td className="py-1.5 font-medium">Importe presentado</td>
                  <td className="py-1.5 text-right font-semibold">{fmtMM(resumen?.importe_presentado_mm)}</td>
                  <td className="py-1.5 text-right text-gray-500">{fmtMM(comp.mes_anterior.resumen.importe_presentado_mm)}</td>
                  <td className="py-1.5 text-right text-gray-500">{fmtMM(comp.mismo_periodo_anio_anterior.resumen.importe_presentado_mm)}</td>
                  <td className="py-1.5 text-right text-gray-300">—</td>
                </tr>
                <tr className="border-t border-gray-100">
                  <td className="py-1.5 font-medium">Importe adjudicado</td>
                  <td className="py-1.5 text-right font-semibold text-[#007934]">{fmtMM(resumen?.importe_adjudicado_mm)}</td>
                  <td className="py-1.5 text-right text-gray-500">{fmtMM(comp.mes_anterior.resumen.importe_adjudicado_mm)}</td>
                  <td className="py-1.5 text-right text-gray-500">{fmtMM(comp.mismo_periodo_anio_anterior.resumen.importe_adjudicado_mm)}</td>
                  <td className="py-1.5 text-right text-gray-500">{presu?.cargado ? fmtMM(presu.importe_mm) : "Sin cargar"}</td>
                </tr>
                <tr className="border-t border-gray-100">
                  <td className="py-1.5 font-medium">Hit rate</td>
                  <td className="py-1.5 text-right font-semibold">{fmtPct(resumen?.hit_rate_pct)}</td>
                  <td className="py-1.5 text-right text-gray-500">{fmtPct(comp.mes_anterior.resumen.hit_rate_pct)}</td>
                  <td className="py-1.5 text-right text-gray-500">{fmtPct(comp.mismo_periodo_anio_anterior.resumen.hit_rate_pct)}</td>
                  <td className="py-1.5 text-right text-gray-300">—</td>
                </tr>
              </tbody>
            </table>
            <p className="text-[10px] text-gray-400 mt-3">
              <span className="font-semibold">Fuente:</span> {data.fuente} · <span className="font-semibold">Corte:</span> {data.corte}
              {/* P0-03 (plan cierre POC/Comité 12/10/2026) · no existe un
                  cierre mensual congelado independiente — todo se calcula en
                  vivo contra el estado actual de la base. Se avisa
                  explícitamente para que nadie interprete un mes pasado como
                  "cerrado e inmutable". */}
              {" · "}<span className="italic">cálculo en vivo contra el estado actual de la base — si un registro de este periodo se corrige más tarde, esta cifra cambiará</span>
            </p>
            <p className="text-[10px] text-gray-400">{data.cobertura?.nota}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Ver contratos de este mes (drill-down a fuente)</h3>
            <div className="flex flex-wrap gap-1.5" data-testid="br-drilldown-buckets">
              {bucketOptions.map((b) => (
                <button key={b} onClick={() => setDrillCtx({ year, month, pipeline: "total", bucket: b, dg: dg || "all" })}
                  data-testid={`br-drilldown-btn-${b}`}
                  className={`text-[10px] px-2 py-1 rounded-full border ${BUCKET_LABELS[b].color} border-transparent hover:opacity-80 flex items-center gap-1`}>
                  {BUCKET_LABELS[b].label} <ExternalLink size={9} />
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {drillCtx && <DrillDownModal ctx={drillCtx} onClose={() => setDrillCtx(null)} />}
    </div>
  );
}
